import './OverlayCanvasManager.css';

export interface Point {
  x: number;
  y: number;
}

export interface AngleMeasurement {
  pointA: Point;
  pointB: Point;
  pointC: Point;
  angle: number;
}

export interface LineMeasurement {
  pointA: Point;
  pointB: Point;
  distance: number;
}

export interface PointMeasurement {
  point: Point;
  label?: string;
}

export interface AngleAxisMeasurement {
  pointA: Point;
  pointB: Point;
  angle: number;
  axisType: 'horizontal' | 'vertical';
  distance: number;
}

export interface LineAxisMeasurement {
  point: Point;
  axisType: 'horizontalLine' | 'verticalLine';
  length: number;
}

export class OverlayCanvasManager {
  private parentCanvas: HTMLCanvasElement;
  private overlayCanvas: HTMLCanvasElement;
  private overlayCtx: CanvasRenderingContext2D;
  private isActive: boolean = false;
  private currentTool: string = '';
  private currentPoints: Point[] = [];
  private measurements: AngleMeasurement[] = [];
  private lineMeasurements: LineMeasurement[] = [];
  private pointMeasurements: PointMeasurement[] = [];
  private angleAxisMeasurements: AngleAxisMeasurement[] = [];
  private lineAxisMeasurements: LineAxisMeasurement[] = [];
  private rafId: number = 0;
  // 镜像功能相关属性
  private isMirrorActive: boolean = false;
  private mirrorAxis: 'horizontal' | 'vertical' = 'horizontal';
  private lastMouseEvent: MouseEvent | null = null;
  private currentScale: number = 1;
  private hoveredMeasurementIndex: number = -1;
  private hoveredLineMeasurementIndex: number = -1;
  private hoveredPointMeasurementIndex: number = -1;
  private hoveredAngleAxisMeasurementIndex: number = -1;

  constructor(parentCanvas: HTMLCanvasElement) {
    this.parentCanvas = parentCanvas;
    this.overlayCanvas = document.createElement('canvas');
    this.overlayCtx = this.overlayCanvas.getContext('2d')!;
    this.setupOverlay();
  }

  private setupOverlay() {
    const parentNode = this.parentCanvas.parentNode;
    if (!parentNode) return;
    
    // 添加 CSS 类名
    this.overlayCanvas.className = 'overlay-canvas';
    
    parentNode.appendChild(this.overlayCanvas);
    this.synchronizeCanvasSize();
  }

  private synchronizeCanvasSize() {
    if (!this.parentCanvas || !this.overlayCanvas) return;
    this.overlayCanvas.width = this.parentCanvas.width;
    this.overlayCanvas.height = this.parentCanvas.height;
  }

  activate(tool: string) {
    if (this.currentTool === tool) return;
    
    if (this.isActive) {
      this.currentTool = tool;
      this.currentPoints = [];
      
      // 根据工具类型设置不同的光标
      if (tool === 'mirror') {
        this.overlayCanvas.classList.remove('cursor-crosshair');
        this.overlayCanvas.classList.add('cursor-default');
      } else {
        this.overlayCanvas.classList.remove('cursor-default');
        this.overlayCanvas.classList.add('cursor-crosshair');
      }
      
      console.log(`Switched to tool: ${tool}`);
      return;
    }
    
    this.isActive = true;
    this.currentTool = tool;
    this.currentPoints = [];
    
    this.overlayCanvas.classList.add('active');
    
    // 根据工具类型设置不同的光标和事件监听
    if (tool === 'mirror') {
      this.overlayCanvas.classList.remove('cursor-crosshair');
      this.overlayCanvas.classList.add('cursor-default');
      // 镜像工具不需要绘图事件监听
    } else {
      this.overlayCanvas.classList.remove('cursor-default');
      this.overlayCanvas.classList.add('cursor-crosshair');
      this.setupEventListeners();
    }
    
    this.startDrawing();
    
    console.log(`Tool activated: ${tool}`);
  }
  
  // 切换镜像功能
  toggleMirror(axis: 'horizontal' | 'vertical' = 'horizontal') {
    this.isMirrorActive = !this.isMirrorActive;
    this.mirrorAxis = axis;
    
    if (this.isMirrorActive) {
      this.activate('mirror');
    } else {
      this.deactivate();
    }
    
    console.log(`Mirror ${this.isMirrorActive ? 'activated' : 'deactivated'} with axis: ${axis}`);
    return this.isMirrorActive;
  }
  
  // 获取当前镜像状态
  isMirrorCurrentlyActive(): boolean {
    return this.isMirrorActive;
  }
  
  // 获取当前镜像轴
  getMirrorAxis(): 'horizontal' | 'vertical' {
    return this.mirrorAxis;
  }
  
  // 重置覆盖层（清除所有测量并隐藏）
  resetOverlay() {
    this.measurements = [];
    this.lineMeasurements = [];
    this.pointMeasurements = [];
    this.angleAxisMeasurements = [];
    this.lineAxisMeasurements = [];
    this.currentPoints = [];
    this.clearOverlay();
    console.log('Overlay reset: all measurements cleared');
  }
  
  // 停用当前工具
  deactivate() {
    if (!this.isActive) return;
    
    this.isActive = false;
    this.currentTool = '';
    this.currentPoints = [];
    
    this.overlayCanvas.classList.remove('active');
    this.removeEventListeners();
    this.stopDrawing();
    this.clearOverlay();
    
    console.log('Tool deactivated');
  }
  
  // 移除事件监听器
  private removeEventListeners() {
    this.overlayCanvas.removeEventListener('mousedown', this.handleMouseDown.bind(this));
    this.overlayCanvas.removeEventListener('contextmenu', this.handleContextMenu.bind(this));
    this.overlayCanvas.removeEventListener('mousemove', this.handleMouseMove.bind(this));
    this.overlayCanvas.removeEventListener('mouseup', this.handleMouseUp.bind(this));
  }

  private setupEventListeners() {
    this.overlayCanvas.addEventListener('mousedown', this.handleMouseDown.bind(this));
    this.overlayCanvas.addEventListener('contextmenu', this.handleContextMenu.bind(this));
    this.overlayCanvas.addEventListener('mousemove', this.handleMouseMove.bind(this));
    this.overlayCanvas.addEventListener('mouseup', this.handleMouseUp.bind(this));
  }

  private startDrawing() {
    if (this.rafId) this.stopDrawing();
    
    const draw = () => {
      if (!this.isActive) return;
      
      this.clearOverlay();
      this.render();
      this.rafId = requestAnimationFrame(draw);
    };
    
    this.rafId = requestAnimationFrame(draw);
  }

  private stopDrawing() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }
  }

  private clearOverlay() {
    this.overlayCtx.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);
  }

  private render() {
    if (!this.isActive) return;
    
    const ctx = this.overlayCtx;
    
    // 根据当前工具类型渲染其特定的内容
    switch (this.currentTool) {
      case 'mirror':
        // 如果是镜像工具，只绘制镜像内容
        this.drawMirrorContent();
        return;
      default:
        // 其他工具正常渲染
        // 如果镜像模式激活，应用镜像变换
        if (this.isMirrorActive) {
          ctx.save();
          
          // 根据镜像轴应用变换
          if (this.mirrorAxis === 'horizontal') {
            // 水平镜像：沿垂直中线翻转
            ctx.translate(this.parentCanvas.width, 0);
            ctx.scale(-1, 1);
          } else {
            // 垂直镜像：沿水平中线翻转
            ctx.translate(0, this.parentCanvas.height);
            ctx.scale(1, -1);
          }
        }
        
        // 渲染所有已完成的测量
        this.renderAllCompletedMeasurements(ctx);
        
        // 根据当前工具类型渲染其特定的内容
        switch (this.currentTool) {
          case 'protractor':
            this.renderProtractorCurrent(ctx);
            break;
          case 'ruler':
            this.renderRulerCurrent(ctx);
            break;
          case 'point':
            this.renderPointCurrent(ctx);
            break;
          case 'vertical':
          case 'horizontal':
            this.renderLineAxisCurrent(ctx);
            break;
          case 'verticalAngle':
          case 'horizontalAngle':
            this.renderAngleAxisCurrent(ctx);
            break;
        }
        
        // 如果应用了镜像变换，恢复上下文状态
        if (this.isMirrorActive) {
          ctx.restore();
        }
    }
  }
  
  // 绘制镜像内容
  private drawMirrorContent() {
    if (!this.parentCanvas || !this.overlayCtx) return;
    
    // 设置镜像变换
    this.overlayCtx.save();
    
    // 根据镜像轴应用变换
    if (this.mirrorAxis === 'horizontal') {
      // 水平镜像：沿垂直中线翻转
      this.overlayCtx.translate(this.parentCanvas.width, 0);
      this.overlayCtx.scale(-1, 1);
    } else {
      // 垂直镜像：沿水平中线翻转
      this.overlayCtx.translate(0, this.parentCanvas.height);
      this.overlayCtx.scale(1, -1);
    }
    
    // 绘制原canvas内容到覆盖层（带镜像变换）
    this.overlayCtx.drawImage(this.parentCanvas, 0, 0);
    
    // 恢复上下文状态
    this.overlayCtx.restore();
  }

  private renderProtractorCurrent(ctx: CanvasRenderingContext2D) {
    this.currentPoints.forEach((point, index) => {
      this.drawPoint(ctx, point, index === 0 ? '#ff4757' : '#007bff', index + 1);
    });

    for (let i = 1; i < this.currentPoints.length; i++) {
      this.drawLine(ctx, this.currentPoints[i-1], this.currentPoints[i], '#ff4757', 3);
    }

    const currentMouse = this.getCurrentMousePosition();
    if (currentMouse) {
      if (this.currentPoints.length >= 3) {
        // 测量完成
      }
      else if (this.currentPoints.length === 1) {
        const lastPoint = this.currentPoints[0];
        this.drawLine(ctx, lastPoint, currentMouse, 'rgba(255, 71, 87, 0.7)', 2);
      }
      else if (this.currentPoints.length === 2) {
        const lastPoint = this.currentPoints[1];
        this.drawLine(ctx, lastPoint, currentMouse, 'rgba(255, 71, 87, 0.7)', 2);
        this.drawAnglePreview(ctx, this.currentPoints[0], this.currentPoints[1], currentMouse);
      }
    }
  }

  private renderRulerCurrent(ctx: CanvasRenderingContext2D) {
    this.currentPoints.forEach((point, index) => {
      this.drawPoint(ctx, point, index === 0 ? '#3498db' : '#e74c3c');
    });

    for (let i = 1; i < this.currentPoints.length; i++) {
      this.drawLine(ctx, this.currentPoints[i-1], this.currentPoints[i], '#e74c3c', 3);
    }

    const currentMouse = this.getCurrentMousePosition();
    if (currentMouse) {
      if (this.currentPoints.length >= 2) {
        // 测量完成
      }
      else if (this.currentPoints.length === 1) {
        const startPoint = this.currentPoints[0];
        this.drawLine(ctx, startPoint, currentMouse, 'rgba(231, 76, 60, 0.7)', 2);
        
        const previewDistance = this.calculateDistance(startPoint, currentMouse);
        const previewMidPoint = {
          x: (startPoint.x + currentMouse.x) / 2,
          y: (startPoint.y + currentMouse.y) / 2
        };
        this.drawDistanceText(ctx, previewMidPoint, previewDistance, 'rgba(52, 152, 219, 0.7)');
      }
    }
  }

  private renderPointCurrent(ctx: CanvasRenderingContext2D) {
    this.currentPoints.forEach((point) => {
      this.drawPoint(ctx, point, '#3498db');
    });
  }

  private renderAngleAxisCurrent(ctx: CanvasRenderingContext2D) {
    this.currentPoints.forEach((point, index) => {
      this.drawPoint(ctx, point, index === 0 ? '#3498db' : '#e74c3c');
    });

    for (let i = 1; i < this.currentPoints.length; i++) {
      this.drawLine(ctx, this.currentPoints[i-1], this.currentPoints[i], '#e74c3c', 3);
    }

    const currentMouse = this.getCurrentMousePosition();
    if (currentMouse) {
      if (this.currentPoints.length >= 2) {
        // 测量完成
      }
      else if (this.currentPoints.length === 1) {
        const startPoint = this.currentPoints[0];
        this.drawLine(ctx, startPoint, currentMouse, 'rgba(231, 76, 60, 0.7)', 2);
        
        const previewDistance = this.calculateDistance(startPoint, currentMouse);
        const previewMidPoint = {
          x: (startPoint.x + currentMouse.x) / 2,
          y: (startPoint.y + currentMouse.y) / 2
        };
        this.drawDistanceText(ctx, previewMidPoint, previewDistance, 'rgba(52, 152, 219, 0.7)');
      }
    }
  }

  private drawPoint(ctx: CanvasRenderingContext2D, point: Point, color: string, number?: number) {
    const pointRadius = 4; // 调整为原来的一半大小
    ctx.beginPath();
    ctx.fillStyle = color;
    ctx.arc(point.x, point.y, pointRadius, 0, 2 * Math.PI);
    ctx.fill();
    
    ctx.beginPath();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.arc(point.x, point.y, pointRadius, 0, 2 * Math.PI);
    ctx.stroke();
    
    if (number) {
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 10px Arial'; // 相应调整字体大小
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(number.toString(), point.x, point.y);
    }
  }

  private drawLine(ctx: CanvasRenderingContext2D, start: Point, end: Point, color: string, width: number = 2) {
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();
  }

  private drawDistanceText(ctx: CanvasRenderingContext2D, point: Point, distance: number, color: string) {
    ctx.fillStyle = color;
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    const text = `${distance.toFixed(1)}px`;
    const textMetrics = ctx.measureText(text);
    
    // 文字背景
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fillRect(
      point.x - textMetrics.width / 2 - 5,
      point.y - 10,
      textMetrics.width + 10,
      20
    );
    
    // 文字
    ctx.fillStyle = color;
    ctx.fillText(text, point.x, point.y);
  }

  private renderAllCompletedMeasurements(ctx: CanvasRenderingContext2D) {
    // 渲染所有量角器测量
    this.measurements.forEach(measurement => {
      this.drawAngleMeasurement(ctx, measurement);
    });

    // 渲染所有直尺测量
    this.lineMeasurements.forEach(line => {
      this.drawLineMeasurement(ctx, line);
    });

    // 渲染所有水平线和垂直线测量
    this.lineAxisMeasurements.forEach(measurement => {
      this.drawLineAxisMeasurement(ctx, measurement);
    });

    // 渲染所有夹角测量
    this.angleAxisMeasurements.forEach(measurement => {
      this.drawAngleAxisMeasurement(ctx, measurement);
    });

    // 渲染所有标点
    this.pointMeasurements.forEach(point => {
      this.drawPoint(ctx, point.point, '#3498db');
    });
  }

  private drawAngleMeasurement(ctx: CanvasRenderingContext2D, measurement: AngleMeasurement) {
    // 绘制线段
    this.drawLine(ctx, measurement.pointA, measurement.pointB, '#ff4757', 3);
    this.drawLine(ctx, measurement.pointB, measurement.pointC, '#ff4757', 3);
    
    // 绘制顶点
    this.drawPoint(ctx, measurement.pointA, '#ff4757', 1);
    this.drawPoint(ctx, measurement.pointB, '#007bff', 2);
    this.drawPoint(ctx, measurement.pointC, '#007bff', 3);
    
    // 绘制角度
    this.drawAngle(ctx, measurement.pointB, measurement.angle, '#ffa502');
  }

  private drawLineMeasurement(ctx: CanvasRenderingContext2D, line: LineMeasurement) {
    // 绘制线段
    this.drawLine(ctx, line.pointA, line.pointB, '#2ecc71', 3);
    
    // 绘制端点
    this.drawPoint(ctx, line.pointA, '#ff4757');
    this.drawPoint(ctx, line.pointB, '#ff4757');
    
    // 绘制距离文字
    const midPoint = {
      x: (line.pointA.x + line.pointB.x) / 2,
      y: (line.pointA.y + line.pointB.y) / 2
    };
    this.drawDistanceText(ctx, midPoint, line.distance, '#3742fa');
  }

  private drawAngleAxisMeasurement(ctx: CanvasRenderingContext2D, measurement: AngleAxisMeasurement) {
    // 绘制线段
    this.drawLine(ctx, measurement.pointA, measurement.pointB, '#9b59b6', 3);
    
    // 绘制端点
    this.drawPoint(ctx, measurement.pointA, '#3498db');
    this.drawPoint(ctx, measurement.pointB, '#e74c3c');
    
    // 绘制距离和角度文字
    const midPoint = {
      x: (measurement.pointA.x + measurement.pointB.x) / 2,
      y: (measurement.pointA.y + measurement.pointB.y) / 2
    };
    
    // 绘制角度箭头和参考线
    this.drawAngleAxisReferenceLines(ctx, measurement);
    this.drawAngleAxisText(ctx, midPoint, measurement, '#8e44ad');
  }

  private drawAngleAxisReferenceLines(ctx: CanvasRenderingContext2D, measurement: AngleAxisMeasurement) {
    const dx = measurement.pointB.x - measurement.pointA.x;
    const dy = measurement.pointB.y - measurement.pointA.y;
    
    if (measurement.axisType === 'vertical') {
      // 绘制垂直参考线（Y轴方向）
      const centerX = measurement.pointA.x;
      const referenceEndY = measurement.pointB.y;
      this.drawReferenceLine(ctx, measurement.pointA, { x: centerX, y: referenceEndY }, '#17CD98');
    } else {
      // 绘制水平参考线（X轴方向）
      const centerY = measurement.pointA.y;
      const referenceEndX = measurement.pointB.x;
      this.drawReferenceLine(ctx, measurement.pointA, { x: referenceEndX, y: centerY }, '#17CD98');
    }
  }

  private drawReferenceLine(ctx: CanvasRenderingContext2D, start: Point, end: Point, color: string) {
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([5, 5]);
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  private drawAngleAxisText(ctx: CanvasRenderingContext2D, point: Point, measurement: AngleAxisMeasurement, color: string) {
    let angleText = '';
    if (measurement.axisType === 'vertical') {
      angleText = `垂直夹角: ${measurement.angle.toFixed(1)}°`;
    } else {
      angleText = `水平夹角: ${measurement.angle.toFixed(1)}°`;
    }
    
    ctx.fillStyle = color;
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // 文字背景
    const textMetrics = ctx.measureText(angleText);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fillRect(
      point.x - textMetrics.width / 2 - 5,
      point.y - 30 - 10,
      textMetrics.width + 10,
      20
    );
    
    // 文字
    ctx.fillStyle = color;
    ctx.fillText(angleText, point.x, point.y - 30);
  }

  private handleMouseDown(event: MouseEvent) {
    if (!this.isActive) return;
    
    event.stopPropagation();
    const point = this.getCanvasPoint(event);
    
    // 根据当前工具类型处理点击事件
    switch (this.currentTool) {
      case 'protractor':
        this.handleProtractorClick(event, point);
        break;
      case 'ruler':
        this.handleRulerClick(event, point);
        break;
      case 'point':
        this.handlePointClick(event, point);
        break;
      case 'vertical':
      case 'horizontal':
        this.handleLineAxisClick(event, point);
        break;
      case 'verticalAngle':
      case 'horizontalAngle':
        this.handleAngleAxisClick(event, point);
        break;
    }
  }

  private handleProtractorClick(event: MouseEvent, point: Point) {
    if (event.button !== 0) return;
    
    if (this.currentPoints.length >= 3) {
      this.completeCurrentAngleMeasurement();
      this.currentPoints = [];
    }
    
    this.currentPoints.push(point);
    console.log(`Point ${this.currentPoints.length} added at (${point.x}, ${point.y})`);
    
    if (this.currentPoints.length === 3) {
      this.completeCurrentAngleMeasurement();
    }
  }

  private handleRulerClick(event: MouseEvent, point: Point) {
    if (event.button !== 0) return;
    
    if (this.currentPoints.length >= 2) {
      this.completeCurrentLineMeasurement();
      this.currentPoints = [];
    }
    
    this.currentPoints.push(point);
    console.log(`Ruler point ${this.currentPoints.length} added at (${point.x}, ${point.y})`);
    
    if (this.currentPoints.length === 2) {
      this.completeCurrentLineMeasurement();
    }
  }

  private handlePointClick(event: MouseEvent, point: Point) {
    if (event.button !== 0) return;
    
    // 创建新的点标记
    const pointMeasurement = {
      point,
      label: `P${this.pointMeasurements.length + 1}`
    };
    
    this.pointMeasurements.push(pointMeasurement);
    this.currentPoints = [];
    console.log('Point marked at:', point);
  }

  private handleAngleAxisClick(event: MouseEvent, point: Point) {
    if (event.button !== 0) return;
    
    if (this.currentPoints.length >= 2) {
      this.completeCurrentAngleAxisMeasurement();
      this.currentPoints = [];
    }
    
    this.currentPoints.push(point);
    console.log(`Angle axis point ${this.currentPoints.length} added at (${point.x}, ${point.y})`);
    
    if (this.currentPoints.length === 2) {
      this.completeCurrentAngleAxisMeasurement();
    }
  }

  private completeCurrentAngleAxisMeasurement() {
    if (this.currentPoints.length !== 2) return;
    
    const distance = this.calculateDistance(this.currentPoints[0], this.currentPoints[1]);
    const dx = this.currentPoints[1].x - this.currentPoints[0].x;
    const dy = this.currentPoints[1].y - this.currentPoints[0].y;
    
    let angle = 0;
    if (this.currentTool === 'verticalAngle') {
      const horizontalAngle = Math.atan2(dy, dx) * (180 / Math.PI);
      angle = 90 - Math.abs(horizontalAngle % 90);
    } else {
      angle = Math.abs(Math.atan2(dy, dx) * (180 / Math.PI));
    }
    
    const measurement: AngleAxisMeasurement = {
      pointA: this.currentPoints[0],
      pointB: this.currentPoints[1],
      angle,
      axisType: this.currentTool === 'verticalAngle' ? 'vertical' : 'horizontal',
      distance
    };
    
    this.angleAxisMeasurements.push(measurement);
    console.log('Angle axis measurement completed:', measurement);
  }

  private completeCurrentLineMeasurement() {
    if (this.currentPoints.length !== 2) return;
    
    const distance = this.calculateDistance(this.currentPoints[0], this.currentPoints[1]);
    const lineMeasurement = {
      pointA: this.currentPoints[0],
      pointB: this.currentPoints[1],
      distance: distance
    };
    
    this.lineMeasurements.push(lineMeasurement);
    console.log('Line measurement completed:', lineMeasurement);
  }

  private completeCurrentAngleMeasurement() {
    if (this.currentPoints.length !== 3) return;
    
    const measurement = {
      pointA: this.currentPoints[0],
      pointB: this.currentPoints[1],
      pointC: this.currentPoints[2],
      angle: this.calculateAnglePoints(
        this.currentPoints[0],
        this.currentPoints[1],
        this.currentPoints[2]
      )
    };
    
    this.measurements.push(measurement);
    console.log('Measurement completed:', measurement);
  }

  private handleContextMenu(event: MouseEvent) {
    if (!this.isActive) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    const clickPoint = this.getCanvasPoint(event);
    
    // 根据工具类型处理右键事件
    switch (this.currentTool) {
      case 'protractor':
        this.handleProtractorContextMenu(clickPoint);
        break;
      case 'ruler':
        this.handleRulerContextMenu(clickPoint);
        break;
      case 'vertical':
      case 'horizontal':
        this.handleLineAxisContextMenu(clickPoint);
        break;
      case 'verticalAngle':
      case 'horizontalAngle':
        this.handleAngleAxisContextMenu(clickPoint);
        break;
      case 'point':
        this.handlePointContextMenu(clickPoint);
        break;
    }
  }

  private handleProtractorContextMenu(clickPoint: Point) {
    const clickedMeasurementIndex = this.findProtractorMeasurementAtPoint(clickPoint);
    
    if (clickedMeasurementIndex >= 0) {
      if (confirm('确定要删除这个角度测量吗？')) {
        this.measurements.splice(clickedMeasurementIndex, 1);
        console.log('Protractor measurement deleted');
      }
    } else {
      if (this.currentPoints.length > 0) {
        this.currentPoints.pop();
        console.log('Cancelled last protractor point. Remaining points:', this.currentPoints.length);
      }
    }
  }

  private handleRulerContextMenu(clickPoint: Point) {
    const clickedLineIndex = this.findRulerMeasurementAtPoint(clickPoint);
    
    if (clickedLineIndex >= 0) {
      if (confirm('确定要删除这个线段测量吗？')) {
        this.lineMeasurements.splice(clickedLineIndex, 1);
        console.log('Ruler measurement deleted');
      }
    } else {
      if (this.currentPoints.length > 0) {
        this.currentPoints.pop();
        console.log('Cancelled last ruler point. Remaining points:', this.currentPoints.length);
      }
    }
  }

  private handleLineAxisContextMenu(clickPoint: Point) {
    const clickedLineAxisIndex = this.findLineAxisMeasurementAtPoint(clickPoint);
    
    if (clickedLineAxisIndex >= 0) {
      if (confirm('确定要删除这条轴线测量吗？')) {
        this.lineAxisMeasurements.splice(clickedLineAxisIndex, 1);
        console.log('Line axis measurement deleted');
      }
    } else {
      if (this.currentPoints.length > 0) {
        this.currentPoints.pop();
        console.log('Cancelled last line axis point. Remaining points:', this.currentPoints.length);
      }
    }
  }

  private handleAngleAxisContextMenu(clickPoint: Point) {
    const clickedAngleAxisIndex = this.findAngleAxisMeasurementAtPoint(clickPoint);
    
    if (clickedAngleAxisIndex >= 0) {
      if (confirm('确定要删除这个夹角测量吗？')) {
        this.angleAxisMeasurements.splice(clickedAngleAxisIndex, 1);
        console.log('Angle axis measurement deleted');
      }
    } else {
      if (this.currentPoints.length > 0) {
        this.currentPoints.pop();
        console.log('Cancelled last angle axis point. Remaining points:', this.currentPoints.length);
      }
    }
  }

  private findLineAxisMeasurementAtPoint(point: Point): number {
    for (let i = 0; i < this.lineAxisMeasurements.length; i++) {
      const measurement = this.lineAxisMeasurements[i];
      
      // 创建轴线的终点来计算距离
      let endPoint: Point;
      if (measurement.axisType === 'verticalLine') {
        endPoint = { x: measurement.point.x, y: measurement.point.y + measurement.length };
      } else {
        endPoint = { x: measurement.point.x + measurement.length, y: measurement.point.y };
      }
      
      if (this.isPointNearLine(point, measurement.point, endPoint, 10)) {
        return i;
      }
    }
    return -1;
  }

  private handlePointContextMenu(clickPoint: Point) {
    const clickedPointIndex = this.findPointMeasurementAtPoint(clickPoint);
    if (clickedPointIndex >= 0) {
      if (confirm('确定要删除这个点标记吗？')) {
        this.pointMeasurements.splice(clickedPointIndex, 1);
        console.log('Point measurement deleted');
      }
    }
  }

  private findProtractorMeasurementAtPoint(point: Point): number {
    for (let i = 0; i < this.measurements.length; i++) {
      const m = this.measurements[i];
      if (this.isPointNearLine(point, m.pointA, m.pointB, 10) || 
          this.isPointNearLine(point, m.pointB, m.pointC, 10)) {
        return i;
      }
    }
    return -1;
  }

  private findRulerMeasurementAtPoint(point: Point): number {
    for (let i = 0; i < this.lineMeasurements.length; i++) {
      const line = this.lineMeasurements[i];
      if (this.isPointNearLine(point, line.pointA, line.pointB, 10)) {
        return i;
      }
    }
    return -1;
  }

  private findAngleAxisMeasurementAtPoint(point: Point): number {
    for (let i = 0; i < this.angleAxisMeasurements.length; i++) {
      const measurement = this.angleAxisMeasurements[i];
      if (this.isPointNearLine(point, measurement.pointA, measurement.pointB, 10)) {
        return i;
      }
    }
    return -1;
  }

  private findPointMeasurementAtPoint(point: Point): number {
    for (let i = 0; i < this.pointMeasurements.length; i++) {
      const p = this.pointMeasurements[i];
      const distance = this.calculateDistance(point, p.point);
      if (distance < 15) {
        return i;
      }
    }
    return -1;
  }

  private handleLineAxisClick(event: MouseEvent, point: Point) {
    if (event.button !== 0) return;
    
    if (this.currentPoints.length >= 1) {
      // 先完成当前测量
      this.completeCurrentLineAxisMeasurement();
      // 清空当前点，不将新点击的点作为下一个测量的起点
      this.currentPoints = [];
      // 直接返回，不添加新点
      return;
    }
    
    this.currentPoints.push(point);
    console.log(`Line axis point added at (${point.x}, ${point.y})`);
  }

  private completeCurrentLineAxisMeasurement() {
    if (this.currentPoints.length !== 1) return;
    
    const currentMouse = this.getCurrentMousePosition();
    if (!currentMouse) return;
    
    const startPoint = this.currentPoints[0];
    let length = 0;
    
    if (this.currentTool === 'vertical') {
      // 垂直线长度 = 鼠标Y坐标 - 起点Y坐标
      length = currentMouse.y - startPoint.y;
    } else {
      // 水平线长度 = 鼠标X坐标 - 起点X坐标
      length = currentMouse.x - startPoint.x;
    }
    
    const measurement: LineAxisMeasurement = {
      point: startPoint,
      axisType: this.currentTool === 'vertical' ? 'verticalLine' : 'horizontalLine',
      length
    };
    
    this.lineAxisMeasurements.push(measurement);
    console.log('Line axis measurement completed:', measurement);
  }

  private renderLineAxisCurrent(ctx: CanvasRenderingContext2D) {
    const currentMouse = this.getCurrentMousePosition();
    
    // 如果正在移动鼠标，显示预览线
    if (currentMouse && this.currentPoints.length === 1) {
      const startPoint = this.currentPoints[0];
      
      if (this.currentTool === 'vertical') {
        // 垂直线：X坐标不变，Y坐标跟随鼠标
        const endPoint = { x: startPoint.x, y: currentMouse.y };
        this.drawLine(ctx, startPoint, endPoint, 'rgba(52, 152, 219, 0.7)', 2);
        this.drawLineLengthText(ctx, startPoint, endPoint, 'rgba(52, 152, 219, 0.7)');
      } else {
        // 水平线：Y坐标不变，X坐标跟随鼠标
        const endPoint = { x: currentMouse.x, y: startPoint.y };
        this.drawLine(ctx, startPoint, endPoint, 'rgba(52, 152, 219, 0.7)', 2);
        this.drawLineLengthText(ctx, startPoint, endPoint, 'rgba(52, 152, 219, 0.7)');
      }
      
      this.drawPoint(ctx, startPoint, '#3498db');
    }
  }

  private drawLineLengthText(ctx: CanvasRenderingContext2D, start: Point, end: Point, color: string) {
    const distance = this.calculateDistance(start, end);
    const midPoint = {
      x: (start.x + end.x) / 2,
      y: (start.y + end.y) / 2
    };
    this.drawDistanceText(ctx, midPoint, distance, color);
  }

  private drawLineAxisMeasurement(ctx: CanvasRenderingContext2D, measurement: LineAxisMeasurement, isHovered: boolean = false) {
    const lineColor = isHovered ? '#ff0000' : (measurement.axisType === 'verticalLine' ? '#3498db' : '#e74c3c');
    const length = measurement.length;
    let endPoint: Point;
    
    if (measurement.axisType === 'verticalLine') {
      // 垂直线：向下延伸
      endPoint = { x: measurement.point.x, y: measurement.point.y + length };
    } else {
      // 水平线：向右延伸
      endPoint = { x: measurement.point.x + length, y: measurement.point.y };
    }
    
    // 绘制线段
    this.drawLine(ctx, measurement.point, endPoint, lineColor, 3);
    
    // 绘制长度文字
    const midPoint = {
      x: (measurement.point.x + endPoint.x) / 2,
      y: (measurement.point.y + endPoint.y) / 2
    };
    this.drawDistanceText(ctx, midPoint, length, isHovered ? '#ff0000' : '#3742fa');
  }

  private isPointNearLine(point: Point, lineStart: Point, lineEnd: Point, threshold: number): boolean {
    const A = point.x - lineStart.x;
    const B = point.y - lineStart.y;
    const C = lineEnd.x - lineStart.x;
    const D = lineEnd.y - lineStart.y;

    const dot = A * C + B * D;
    const len_sq = C * C + D * D;
    let param = -1;
    if (len_sq !== 0) param = dot / len_sq;

    let xx, yy;

    if (param < 0) {
      xx = lineStart.x;
      yy = lineStart.y;
    } else if (param > 1) {
      xx = lineEnd.x;
      yy = lineEnd.y;
    } else {
      xx = lineStart.x + param * C;
      yy = lineStart.y + param * D;
    }

    const dx = point.x - xx;
    const dy = point.y - yy;
    return Math.sqrt(dx * dx + dy * dy) < threshold;
  }

  private handleMouseMove(event: MouseEvent) {
    if (!this.isActive) return;
    
    event.stopPropagation();
    this.lastMouseEvent = event;
  }

  private handleMouseUp(event: MouseEvent) {
    if (!this.isActive) return;
    event.stopPropagation();
  }

  private getCurrentMousePosition(): Point | null {
    if (!this.lastMouseEvent) return null;
    return this.getCanvasPoint(this.lastMouseEvent);
  }

  private getCanvasPoint(event: MouseEvent): Point {
    const rect = this.overlayCanvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  }

  // ... 其他方法实现 ...

  private drawAnglePreview(ctx: CanvasRenderingContext2D, A: Point, B: Point, C: Point) {
    const angle = this.calculateAnglePoints(A, B, C);
    
    const angleBA = Math.atan2(A.y - B.y, A.x - B.x);
    const angleBC = Math.atan2(C.y - B.y, C.x - B.x);
    
    ctx.beginPath();
    ctx.strokeStyle = 'rgba(255, 165, 2, 0.7)';
    ctx.lineWidth = 2;
    ctx.arc(B.x, B.y, 25, angleBA, angleBC);
    ctx.stroke();
    
    this.drawAngleText(ctx, B, angle, 'rgba(55, 66, 250, 0.7)');
  }

  private drawAngleAxisPreview(ctx: CanvasRenderingContext2D, A: Point, B: Point, angle: number) {
    const midPoint = {
      x: (A.x + B.x) / 2,
      y: (A.y + B.y) / 2
    };
    
    ctx.fillStyle = 'rgba(142, 68, 173, 0.7)';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${angle.toFixed(1)}°`, midPoint.x, midPoint.y);
  }

  private calculateAnglePoints(A: Point, B: Point, C: Point): number {
    const BAx = A.x - B.x;
    const BAy = A.y - B.y;
    const BCx = C.x - B.x;
    const BCy = C.y - B.y;

    const dotProduct = BAx * BCx + BAy * BCy;
    const magnitudeBA = Math.sqrt(BAx * BAx + BAy * BAy);
    const magnitudeBC = Math.sqrt(BCx * BCx + BCy * BCy);

    const cosTheta = dotProduct / (magnitudeBA * magnitudeBC);
    const angleRad = Math.acos(Math.max(Math.min(cosTheta, 1), -1));
    
    return angleRad * (180 / Math.PI);
  }

  private drawAngle(ctx: CanvasRenderingContext2D, center: Point, angle: number, color: string) {
    const angleBA = Math.atan2(-1, 0); // 从正X轴开始
    const angleBC = angleBA + (angle * Math.PI / 180);
    
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.arc(center.x, center.y, 25, angleBA, angleBC);
    ctx.stroke();
    
    this.drawAngleText(ctx, center, angle, '#3742fa');
  }

  private drawAngleText(ctx: CanvasRenderingContext2D, center: Point, angle: number, color: string) {
    ctx.fillStyle = color;
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    const text = `${angle.toFixed(1)}°`;
    
    const textMetrics = ctx.measureText(text);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fillRect(
      center.x - textMetrics.width / 2 - 5,
      center.y - 25 - 10,
      textMetrics.width + 10,
      20
    );
    
    ctx.fillStyle = color;
    ctx.fillText(text, center.x, center.y - 25);
  }

  private calculateDistance(pointA: Point, pointB: Point): number {
    const dx = pointB.x - pointA.x;
    const dy = pointB.y - pointA.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // ... 其他方法实现 ...
}